import { Button, Color, Font, Spacing, Text, shortDate } from 'zens-storybook'
import React, { useMemo, useState } from 'react'
import { StyleSheet, View, ViewProps } from 'react-native'
import type { NormalizedCompanyColorScheme } from 'zens-store'
import {
  programColorsForDay,
  ScheduleVisualProgram,
  ScheduleWeekday,
  ScheduleWeekdays,
  Trigger,
  useStore,
} from 'zens-store'
import { addDays, addWeeks, isToday, startOfWeek, subWeeks } from 'date-fns'

import { useTranslation } from 'react-i18next'

import ScheduleSelection from '../schedule/selection'
import ScheduleLegend, { ProgramType, ScheduleLegendItem } from '../schedule/legend'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flexDirection: 'row',
  },
  schedule: {
    flex: 1,
    marginLeft: Spacing.XS,
  },
  date: {
    width: 80,
  },
  head: {
    height: 18,
  },
  headText: {
    color: Color.metalGrey,
  },
  row: {
    alignItems: 'flex-start',
    flexDirection: 'row',
  },
  selection: {
    height: 16,
    lineHeight: 16,
    marginVertical: Spacing.XXS / 2,
  },
  col: {
    flex: 1,
    flexDirection: 'row',
  },
  colLeft: {
    flex: 11,
  },
  colRight: {
    flex: 1,
  },
  colContent: {
    marginHorizontal: -20,
    alignItems: 'center',
  },
  colFirst: {
    alignItems: 'center',
    marginHorizontal: -20,
    width: 40,
  },
  day: {
    textAlign: 'right',
    color: Color.metalGrey,
  },
  today: {
    fontFamily: Font.medium,
  },
  legend: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: Spacing.S,
  },
  legendOption: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  legendMarker: {
    width: 12,
    height: 12,
    marginRight: Spacing.XS,
    borderRadius: 2,
  },
  legendText: {
    lineHeight: 16,
  },
  controls: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: Spacing.XS,
    marginLeft: 80,
  },
})

interface Props extends ViewProps {
  schedule?: Record<ScheduleWeekday, ScheduleVisualProgram>
  triggers?: Trigger[]
  colorScheme: NormalizedCompanyColorScheme
  types?: ProgramType[]
  onChangeTypes?: (types: ProgramType[]) => void
}

const COLOR_GROUP_ORDER: (keyof NormalizedCompanyColorScheme)[] = ['schedule_colors', 'trigger_colors']
const labelForMeaning = (meaning: string) => meaning.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())

function displayColorForWorkspace(colorKey: string) {
  const normalizedColorKey = colorKey.toLowerCase()
  if (normalizedColorKey === 'red') return Color.soothingPink
  if (normalizedColorKey === 'blue') return Color.lightGreyBlue
  return Color[colorKey as keyof typeof Color] || Color.calmGrey
}

function displaySelectionColorForWorkspace(color: string) {
  if (color === Color.red) return Color.soothingPink
  if (color === Color.blue) return Color.lightGreyBlue
  return color
}

const WorkspaceProgram = (props: Props) => {
  const { i18n } = useStore()
  const { schedule, triggers, colorScheme, types, onChangeTypes, style, ...otherProps } = props
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }))
  const { t } = useTranslation()
  const legendItems = useMemo<ScheduleLegendItem[]>(() => {
    const items: ScheduleLegendItem[] = []
    const seen = new Set<string>()
    for (const groupKey of COLOR_GROUP_ORDER) {
      const group = colorScheme[groupKey]
      if (!group) continue
      for (const [meaning, colorKey] of Object.entries(group)) {
        if (seen.has(meaning)) continue
        seen.add(meaning)
        items.push({
          value: meaning,
          title: (
            <View style={styles.legendOption}>
              <View style={[styles.legendMarker, { backgroundColor: displayColorForWorkspace(colorKey) }]} />
              <Text style={styles.legendText}>{labelForMeaning(meaning)}</Text>
            </View>
          ),
        })
      }
    }
    return items
  }, [colorScheme, t])

  const normalizedTypes = useMemo(() => {
    if (!types) return types
    const legendValues = new Set(legendItems.map((item) => item.value))
    return [...new Set(types.filter((type) => legendValues.has(type)))]
  }, [legendItems, types])

  const dayColors = useMemo(() => {
    return new Array(7).fill(null).map((_, index) => {
      const date = addDays(weekStart, index)
      const scheduleDay = schedule?.[ScheduleWeekdays[index]]
      const colors = programColorsForDay(date, scheduleDay, triggers, colorScheme, normalizedTypes)
      return colors.map(displaySelectionColorForWorkspace)
    })
  }, [colorScheme, normalizedTypes, schedule, triggers, weekStart])

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.controls}>
        <Button
          light
          size="S"
          left="left"
          onPress={() => setWeekStart(subWeeks(weekStart, 1))}
          testID="WorkspaceProgram.Previous"
        />
        <Button
          light
          size="S"
          title={t('Today')}
          onPress={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
          testID="WorkspaceProgram.Today"
        />
        <Button
          light
          size="S"
          left="right"
          onPress={() => setWeekStart(addWeeks(weekStart, 1))}
          testID="WorkspaceProgram.Next"
        />
      </View>
      <View style={styles.content}>
        <View style={styles.date}>
          <View style={styles.head} />
          {Array(7)
            .fill('')
            .map((_, index) => {
              const date = addDays(weekStart, index)
              return (
                <Text
                  key={`week-${index}`}
                  style={[styles.selection, styles.day, isToday(date) && styles.today]}
                  level={7}
                >
                  {shortDate(date, i18n.language)}
                </Text>
              )
            })}
        </View>
        <View style={styles.schedule}>
          <View style={[styles.row, styles.head]}>
            <View style={styles.colFirst}>
              <Text numberOfLines={1} level={7} style={styles.headText}>
                0
              </Text>
            </View>
            {Array(12)
              .fill('')
              .map((_, index) => (
                <View style={styles.col} key={`head-${index}`}>
                  <View style={styles.colLeft} />
                  <View style={styles.colRight}>
                    <View style={styles.colContent}>
                      <Text numberOfLines={1} level={7} style={styles.headText}>
                        {((index + 1) * 2) % 24}
                      </Text>
                    </View>
                  </View>
                </View>
              ))}
          </View>
          {dayColors.map((colors, index) => {
            return (
              <View
                key={`day-${index}`}
                style={[styles.row, styles.selection, { zIndex: (ScheduleWeekdays.length - index) * 100 }]}
              >
                <ScheduleSelection colors={colors} />
              </View>
            )
          })}
          <ScheduleLegend style={styles.legend} value={normalizedTypes} onChange={onChangeTypes} items={legendItems} />
        </View>
      </View>
    </View>
  )
}

export default WorkspaceProgram
